import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Package, Truck, CheckCircle, Clock, MapPin, Phone, Mail, Calendar, AlertCircle, RefreshCw } from 'lucide-react';
import './OrderTracking.css';

function OrderTracking() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trackingUpdates, setTrackingUpdates] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadOrderData();
  }, [orderId]);

  const loadOrderData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load order details
      const orderDoc = await getDoc(doc(db, 'orders', orderId));
      if (!orderDoc.exists()) {
        setError('Order not found');
        return;
      }

      const orderData = { id: orderDoc.id, ...orderDoc.data() };
      setOrder(orderData);

      // Load tracking updates
      await loadTrackingUpdates(orderId);
    } catch (err) {
      console.error('Error loading order:', err);
      setError('Failed to load order information');
    } finally {
      setLoading(false);
    }
  };

  const loadTrackingUpdates = async (orderId) => {
    try {
      const updatesQuery = query(
        collection(db, 'orders', orderId, 'tracking'),
        orderBy('timestamp', 'desc'),
        limit(20)
      );
      
      const updatesSnapshot = await getDocs(updatesQuery);
      const updates = updatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTrackingUpdates(updates);
    } catch (err) {
      console.error('Error loading tracking updates:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadOrderData();
    setRefreshing(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="status-icon confirmed" />;
      case 'processing':
        return <Package className="status-icon processing" />;
      case 'shipped':
        return <Truck className="status-icon shipped" />;
      case 'delivered':
        return <CheckCircle className="status-icon delivered" />;
      case 'cancelled':
        return <AlertCircle className="status-icon cancelled" />;
      default:
        return <Clock className="status-icon pending" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return '#3b82f6';
      case 'processing':
        return '#f59e0b';
      case 'shipped':
        return '#8b5cf6';
      case 'delivered':
        return '#10b981';
      case 'cancelled':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed':
        return 'Order Confirmed';
      case 'processing':
        return 'Processing';
      case 'shipped':
        return 'Shipped';
      case 'delivered':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Pending';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="order-tracking-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading order information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-tracking-container">
        <div className="error-container">
          <AlertCircle size={48} />
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={handleRefresh} className="refresh-btn">
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-tracking-container">
        <div className="error-container">
          <Package size={48} />
          <h3>Order Not Found</h3>
          <p>The order you're looking for doesn't exist.</p>
          <Link to="/orders" className="back-btn">Back to Orders</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="order-tracking-container">
      <div className="tracking-header">
        <div className="header-content">
          <div className="order-info">
            <h1>Order Tracking</h1>
            <div className="order-number">
              <Package size={20} />
              <span>Order #{order.id}</span>
            </div>
          </div>
          <button onClick={handleRefresh} className={`refresh-btn ${refreshing ? 'spinning' : ''}`}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      <div className="tracking-content">
        {/* Current Status */}
        <div className="status-card">
          <div className="status-header">
            <h2>Current Status</h2>
            <span className="status-date">Updated: {formatDate(order.updatedAt)}</span>
          </div>
          <div className="status-display">
            {getStatusIcon(order.status)}
            <div className="status-details">
              <h3 style={{ color: getStatusColor(order.status) }}>
                {getStatusText(order.status)}
              </h3>
              <p>{order.statusMessage || 'Your order is being processed'}</p>
            </div>
          </div>
        </div>

        {/* Tracking Number */}
        {order.trackingNumber && (
          <div className="tracking-number-card">
            <h3>Tracking Number</h3>
            <div className="tracking-number">
              <Truck size={20} />
              <span>{order.trackingNumber}</span>
              <button 
                onClick={() => navigator.clipboard.writeText(order.trackingNumber)}
                className="copy-btn"
              >
                Copy
              </button>
            </div>
            {order.carrier && (
              <p className="carrier-info">Carrier: {order.carrier}</p>
            )}
          </div>
        )}

        {/* Order Details */}
        <div className="order-details-card">
          <h3>Order Details</h3>
          <div className="order-items">
            {order.items?.map((item, index) => (
              <div key={index} className="order-item">
                <div className="item-info">
                  <h4>{item.name}</h4>
                  <p>Size: {item.size} | Color: {item.color}</p>
                  <p>Quantity: {item.quantity}</p>
                </div>
                <div className="item-price">
                  ${(item.price * item.quantity).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
          <div className="order-summary">
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>${order.subtotal?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="summary-row">
              <span>Tax:</span>
              <span>${order.tax?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="summary-row">
              <span>Shipping:</span>
              <span>${order.shipping?.cost?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="summary-row total">
              <span>Total:</span>
              <span>${order.total?.toFixed(2) || '0.00'}</span>
            </div>
          </div>
        </div>

        {/* Shipping Address */}
        <div className="shipping-card">
          <h3>Shipping Information</h3>
          <div className="shipping-details">
            <div className="address-section">
              <h4>Shipping Address</h4>
              <div className="address">
                <MapPin size={16} />
                <div>
                  <p>{order.customer?.name}</p>
                  <p>{order.customer?.address}</p>
                  {order.customer?.address2 && <p>{order.customer.address2}</p>}
                  <p>{order.customer?.city}, {order.customer?.state} {order.customer?.zipCode}</p>
                  <p>{order.customer?.country}</p>
                </div>
              </div>
            </div>
            <div className="contact-section">
              <h4>Contact Information</h4>
              <div className="contact-info">
                <div className="contact-item">
                  <Mail size={16} />
                  <span>{order.customer?.email}</span>
                </div>
                <div className="contact-item">
                  <Phone size={16} />
                  <span>{order.customer?.phone}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tracking Timeline */}
        <div className="timeline-card">
          <h3>Order Timeline</h3>
          <div className="timeline">
            {trackingUpdates.length > 0 ? (
              trackingUpdates.map((update, index) => (
                <div key={update.id} className="timeline-item">
                  <div className="timeline-marker">
                    {getStatusIcon(update.status)}
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <h4>{getStatusText(update.status)}</h4>
                      <span className="timeline-date">{formatDate(update.timestamp)}</span>
                    </div>
                    <p>{update.message}</p>
                    {update.location && (
                      <p className="timeline-location">
                        <MapPin size={14} />
                        {update.location}
                      </p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-updates">
                <Clock size={24} />
                <p>No tracking updates available yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Order Dates */}
        <div className="dates-card">
          <h3>Order Timeline</h3>
          <div className="order-dates">
            <div className="date-item">
              <Calendar size={16} />
              <div>
                <p>Order Placed</p>
                <span>{formatDate(order.createdAt)}</span>
              </div>
            </div>
            {order.shippedAt && (
              <div className="date-item">
                <Truck size={16} />
                <div>
                  <p>Shipped</p>
                  <span>{formatDate(order.shippedAt)}</span>
                </div>
              </div>
            )}
            {order.deliveredAt && (
              <div className="date-item">
                <CheckCircle size={16} />
                <div>
                  <p>Delivered</p>
                  <span>{formatDate(order.deliveredAt)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderTracking;
